import { FC, useMemo } from 'react';
import { Button, Flex } from '@chakra-ui/react';
import { useChatFeatureFlag } from 'featureFlags';
import { routes } from 'app/routing/routesConfig';
import { Song } from 'types/song';
import { ChatChannelUser } from 'types/chat.types';
import { getUserGuid } from 'app/store/Auth/selectors';
import { getShowSplits } from 'app/store/SongList/selectors';
import { getCurrentChannel } from 'app/store/Chat/selectors';
import { useAppSelector } from 'app/store';
import { ChatMembers } from 'app/components/Chat/ChatMembers/ChatMembers';
import { NavTabs } from 'app/components/Core/ChakraNavTabs';

interface SongNavTabsProps {
  song: Song;
  onChatOpen: () => void;
}

export const SongNavTabs: FC<SongNavTabsProps> = ({ song, onChatOpen }) => {
  const showSplits = useAppSelector(getShowSplits);
  const channelInfo = useAppSelector(getCurrentChannel);
  const currentUserGuid = useAppSelector(getUserGuid);
  const { chatFeatureEnabled } = useChatFeatureFlag();

  const activeUsers = useMemo(() => {
    return (channelInfo?.channelUsers || [])
      .filter(
        (user: ChatChannelUser) =>
          user.userGuid !== currentUserGuid && Boolean(user.profileCard)
      )
      .map((user: ChatChannelUser) => user.profileCard);
  }, [channelInfo?.channelUsers, currentUserGuid]);

  return (
    <Flex id="song-nav-tabs" position="relative">
      <NavTabs
        ml={160}
        routes={[
          routes.songAudioList,
          routes.songLyrics,
          routes.songContributions,
          ...(showSplits ? [routes.songSplits] : []),
          routes.songCollaborators,
          routes.songDetails,
        ]}
        guid={song.guid}
      />
      {chatFeatureEnabled && channelInfo && (
        <Button
          position="absolute"
          right={6}
          opacity={0.54}
          _hover={{ opacity: 1 }}
          variant="text"
          size="auto"
          zIndex={1}
          data-testid="messenger-open-button"
          onClick={onChatOpen}
        >
          <ChatMembers
            users={activeUsers || []}
            maxGroupSize={3}
            unreadMessageCount={0}
            showUnreadIndicator={channelInfo?.unreadMessageCount > 0}
          />
        </Button>
      )}
    </Flex>
  );
};
